import React, { useState } from 'react';
import { v4 as uuid } from 'uuid';
import { DragDropContext } from 'react-beautiful-dnd';
import { ContentState, EditorState } from 'draft-js';
import htmlToDraft from 'html-to-draftjs';

import { LanguageIsoCode } from 'utils/enums';
import { SurveyElement, MultiTextBoxItemType } from 'utils/surveyBuilder/enums';
import AccordionEditor from 'components/AccordionEditor';
import DragItem from 'components/DragAndDrop/DragItem';
import DropArea from 'components/DragAndDrop/DropArea';
import Select from 'components/Select';
import TextEditor from 'components/TextEditor';

import classes from './MultiTextBoxItem.module.scss';

const addItemSelectStyles = {
  control: (base) => ({
    ...base,
    border: 'none',
  }),
};

const itemOptions = {
  [MultiTextBoxItemType.TEXT_BOX]: {
    value: MultiTextBoxItemType.TEXT_BOX,
    label: 'Text Box',
  },
  [MultiTextBoxItemType.ACCORDION]: {
    value: MultiTextBoxItemType.ACCORDION,
    label: 'Accordion',
  },
};

const addItemSelectOptions = Object.values(itemOptions);

const getInitialEditorStates = (items, selectedLanguage) => {
  const initialStates = {};

  items.forEach((item) => {
    if (item.type === MultiTextBoxItemType.TEXT_BOX && !initialStates[item.id]) {
      if (!item.richText) {
        initialStates[item.id] = EditorState.createEmpty();
      } else {
        const richText =
          item.richText[selectedLanguage] || item.richText[LanguageIsoCode.EN];
        const blocksFromHtml = htmlToDraft(richText);
        const { contentBlocks, entityMap } = blocksFromHtml;
        const contentState = ContentState.createFromBlockArray(
          contentBlocks,
          entityMap
        );

        const editorState = EditorState.createWithContent(contentState);

        initialStates[item.id] = editorState;
      }
    }
  });

  return initialStates;
};

const MultiTextBoxItem = ({
  itemId,
  onRichTextChange,
  onAddItem,
  onChange,
  items = [],
  selectedLanguage,
}) => {
  const [localItems, setLocalItems] = useState(items);
  const [editorStates, setEditorStates] = useState(() =>
    getInitialEditorStates(items, selectedLanguage)
  );

  const onSelectItemOptionHandler = (value) => {
    const newItem = {
      id: uuid(),
      type: value,
    };

    if (value === MultiTextBoxItemType.ACCORDION) {
      newItem.content = { [selectedLanguage]: '' };
      newItem.richText = { [selectedLanguage]: '' };
    }

    if (value === MultiTextBoxItemType.ACCORDION) {
      newItem.items = [
        {
          id: uuid(),
          title: {
            content: { [selectedLanguage]: '' },
          },
          body: {
            content: { [selectedLanguage]: '' },
            richText: { [selectedLanguage]: '' },
          },
        },
      ];
    }

    onAddItem(newItem);
    setLocalItems((prev) => [...prev, newItem]);

    if (value === MultiTextBoxItemType.TEXT_BOX) {
      setEditorStates((prev) => ({
        ...prev,
        [newItem.id]: EditorState.createEmpty(),
      }));
    }
  };

  const onDeleteItemHandler = (id) => {
    const updatedItems = localItems.filter((item) => item.id !== id);
    setLocalItems(updatedItems);
    setEditorStates((prev) => {
      const newStates = { ...prev };
      delete newStates[id];
      return newStates;
    });
    onChange(id, { items: updatedItems });
  };

  const onEditorStateChange = (id) => (editorState) => {
    setEditorStates((prev) => ({
      ...prev,
      [id]: editorState,
    }));
    onRichTextChange(id, editorState);
  };

  const onAccordionItemsChange = (id, newAccordionItems) => {
    const updatedItems = localItems.map((item) => {
      if (item.id === id) {
        const updatedItem = {
          ...item,
          items: newAccordionItems,
        };
        onChange(id, updatedItem);
        return updatedItem;
      }
      return item;
    });
    setLocalItems(updatedItems);
  };

  const onDragEnd = ({ destination, source }) => {
    if (!destination) return;

    const newItems = Array.from(localItems);
    const [removed] = newItems.splice(source.index, 1);
    newItems.splice(destination.index, 0, removed);

    setLocalItems(newItems);
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <DropArea dropId={itemId.toString()} type={SurveyElement.MULTI_TEXT_BOX}>
        <Select
          className={classes['add-item-select']}
          isSearchable={false}
          value={null}
          styles={addItemSelectStyles}
          options={addItemSelectOptions}
          onChange={({ value }) => onSelectItemOptionHandler(value)}
        />
        <div className={classes.items}>
          {localItems.map(({ id, type, items: accordionItems }, index) => (
            <DragItem key={id} dragId={String(id)} index={index}>
              {type === MultiTextBoxItemType.TEXT_BOX && (
                <div className={classes['text-box-item']}>
                  <TextEditor
                    textEditorState={{
                      editorState:
                        editorStates[id] || EditorState.createEmpty(),
                    }}
                    onEditorStateChange={onEditorStateChange(id)}
                    title="Text Box"
                    onDelete={() => onDeleteItemHandler(id)}
                  />
                </div>
              )}
              {type === MultiTextBoxItemType.ACCORDION && (
                <AccordionEditor
                  items={accordionItems || []}
                  onItemsChange={(newItems) =>
                    onAccordionItemsChange(id, newItems)
                  }
                  onDelete={() => onDeleteItemHandler(id)}
                  selectedLanguage={selectedLanguage}
                />
              )}
            </DragItem>
          ))}
        </div>
      </DropArea>
    </DragDropContext>
  );
};

export default MultiTextBoxItem;
